import { useContext, useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { AdminPanelSettings, Hub, List, Lock, Psychology } from '@mui/icons-material'
import { Alert, AlertTitle, Button, Collapse } from '@mui/material'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../io/context/EntityTypes'
import usePermittedActions from '../io/usePermittedActions'
import SnackBarProvider from '../components/feedback/SnackBarProvider'
import DrawerScaffold, { DrawerItem, DrawerSection } from '../components/layout/DrawerScaffold'
import LoadingOverlay from '../components/feedback/LoadingOverlay'
import { iconForEntityTypeKind } from '../components/misc/entityTypeIcons'
import { isAdminUserType } from '../util/entityType/isAdminUserType'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const LandingPage = () => {
  const entityTypeRepository = useContext(EntityTypesContext)

  useEffect(() => {
    entityTypeRepository.getAll().then()
  }, [])

  const requested = useRef([
    new PermittedGlobalAction(GlobalAction.ManageForms),
    new PermittedGlobalAction(GlobalAction.ManageEntityTypes),
    new PermittedGlobalAction(GlobalAction.ManagePermissionGrants),
    new PermittedGlobalAction(GlobalAction.ManageAuthentication),
  ])
  const [permissions, fetchPermissionsError] = usePermittedActions(requested.current)

  const entityTypes = entityTypeRepository.useSortedEntityTypes() ?? []

  const error = entityTypeRepository.useError()
  const loading = entityTypeRepository.useLoading()

  if (error) {
    return (
      <SnackBarProvider>
        <Collapse in={!!error}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  await entityTypeRepository.clearError()
                  await entityTypeRepository.refreshAll()
                }}
              >
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load entities. Please try again.</AlertTitle>
            <div>{String(error)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  if (fetchPermissionsError) {
    return (
      <SnackBarProvider>
        <Collapse in={!!fetchPermissionsError}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button color="inherit" size="small" onClick={async () => window.location.reload()}>
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load permissions. Please reload the page.</AlertTitle>
            <div>{String(fetchPermissionsError)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  const hasGlobalPermission = (action: GlobalAction) =>
    permissions?.some((permission) => permission.equals(new PermittedGlobalAction(action)))

  const createEntityTypeItem = (
    entityType: EntityType,
    icon = iconForEntityTypeKind(entityType.kind, <List />)
  ): DrawerItem => ({
    icon,
    text: entityType.name,
    key: `type-${entityType.id.toString()}`,
    path: `/entities/${entityType.id.toString()}`,
  })

  const canManageEntityTypes = hasGlobalPermission(GlobalAction.ManageEntityTypes)
  const canManagePermissionGrants = hasGlobalPermission(GlobalAction.ManagePermissionGrants)
  const canManageAdminUsers = hasGlobalPermission(GlobalAction.ManageAuthentication)

  const adminUsersEntityType = entityTypes.find(
    (type) => type.kind === EntityTypeKind.User && isAdminUserType(type.name)
  )

  const adminItems: DrawerItem[] = []
  if (canManageEntityTypes) {
    adminItems.push({
      icon: <Hub />,
      text: 'Types',
      key: 'types',
      path: `/types`,
    })
    adminItems.push({
      icon: <Psychology />,
      text: 'Semantics',
      key: 'semantics',
      path: `/semantics`,
    })
  }
  if (canManagePermissionGrants) {
    adminItems.push({
      icon: <Lock />,
      text: 'Access Control',
      key: 'access-control',
      path: `/access-control`,
    })
  }
  if (canManageAdminUsers && adminUsersEntityType) {
    adminItems.push({
      icon: <AdminPanelSettings />,
      text: 'Admin Users',
      key: `admin-user-${adminUsersEntityType.id.toString()}`,
      path: `/entities/${adminUsersEntityType.id.toString()}`,
    })
  }

  const adminEntityTypeId = adminUsersEntityType?.id.toString()
  const excludeAdminEntityType = adminEntityTypeId ? new Set([adminEntityTypeId]) : new Set<string>()

  const entityTypeItemsByKind = (kind: EntityTypeKind, exclusions: Set<string> = new Set()): DrawerItem[] =>
    entityTypes
      .filter((type) => type.kind === kind && !exclusions.has(type.id.toString()))
      .map((type) => createEntityTypeItem(type))

  const userItems = entityTypeItemsByKind(EntityTypeKind.User, excludeAdminEntityType)
  const schoolItems = entityTypeItemsByKind(EntityTypeKind.School)
  const genericItems = entityTypeItemsByKind(EntityTypeKind.Generic)
  const learningUnitItems = entityTypeItemsByKind(EntityTypeKind.LearningUnit)
  const formItems = entityTypeItemsByKind(EntityTypeKind.Form)

  const sections: DrawerSection[] = []

  const visibleEntities = [userItems, schoolItems, genericItems].some((items) => items.length > 0)
  const visibleDocuments = [learningUnitItems, formItems].some((items) => items.length > 0)

  if (adminItems.length) {
    sections.push({
      id: 'administration',
      title: 'Administration',
      items: adminItems,
      dividerBefore: false,
    })
  }

  const pushSection = (id: string, title: string, items: DrawerItem[], dividerBefore: boolean) => {
    if (!items.length) return
    sections.push({ id, title, items, dividerBefore })
  }

  const entitySections: { id: string; title: string; items: DrawerItem[] }[] = [
    { id: 'users', title: 'Users', items: userItems },
    { id: 'schools', title: 'Schools', items: schoolItems },
    { id: 'others', title: 'Others', items: genericItems },
  ]
  const visibleEntitySections = entitySections.filter(({ items }) => items.length > 0)
  visibleEntitySections.forEach(({ id, title, items }, index) => {
    pushSection(id, title, items, index === 0 && sections.length > 0)
  })

  const documentSections: { id: string; title: string; items: DrawerItem[] }[] = [
    { id: 'learning-units', title: 'Learning Units', items: learningUnitItems },
    { id: 'forms', title: 'Forms', items: formItems },
  ]
  const visibleDocumentSections = documentSections.filter(({ items }) => items.length > 0)
  visibleDocumentSections.forEach(({ id, title, items }, index) => {
    const dividerBefore = index === 0 ? visibleEntities && visibleDocuments : false
    pushSection(id, title, items, dividerBefore)
  })

  return (
    <SnackBarProvider>
      <DrawerScaffold sections={sections}>
        <Outlet />
      </DrawerScaffold>
      <LoadingOverlay isOpen={loading} />
    </SnackBarProvider>
  )
}

export default LandingPage
